import { useRef, useState, useMemo, type ChangeEvent } from 'react'
import {
  Avatar,
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAccessCheck } from '@/hooks/useAccess'

const MEMBERS_API_PATH = '/access-control/users/members'

export default function AgentStatusPage() {
  const colors = useThemeColors()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const access = useAccessCheck()

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      showActionsColumn: false,
      dataFields: {
        first_name: {
          name: 'Name',
          withSort: false,
          maxWidth: '220px',
        },
        user_email: {
          name: 'Email',
          withSort: false,
          maxWidth: '240px',
        },
        role_name: {
          name: 'Role',
          withSort: false,
          maxWidth: '140px',
        },
        assigned_tickets_count: {
          name: 'Assigned Tickets',
          withSort: false,
          maxWidth: '120px',
        },
      },
      dataSource: MEMBERS_API_PATH,
      primaryKey: 'id',
      softDelete: false,
      displayInModal: false,
      noRecordText: 'No team members found. Users reporting to you will appear here.',
      hideSelection: true,
      otherActions: [],
    }),
    []
  )

  const permissions = useMemo(
    () => ({
      can_edit: false,
      can_delete: false,
      can_create: false,
      can_view: access.can_view,
      can_export: false,
      can_import: false,
    }),
    [access.can_view]
  )

  const tableParams = useMemo(
    () => ({
      per_page: 10,
      ...(searchQuery ? { search: searchQuery } : {}),
    }),
    [searchQuery]
  )

  const handleSearch = () => {
    tableRef.current?.applyFilter('search', searchQuery || '')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  return (
    <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
      <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
        <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
          <CardHeader flexShrink={0} py={3}>
            <Heading size="md" mb={1}>
              Agent Status
            </Heading>
            <Text fontSize="sm" color={colors.text}>
              Team members reporting to you and their assigned ticket count
            </Text>
          </CardHeader>

          <CardHeader py={0} px={0} flexShrink={0}>
            <Box pt={2} pb={0} px={4}>
              <Box borderBottom="2px" borderColor={colors.border} />
            </Box>
          </CardHeader>

          <CardHeader py={1} pt={3} pb={2} flexShrink={0} px={5}>
            <HStack spacing={4} flexWrap="wrap">
              <Box flex="0 1 auto" minW="250px" maxW="400px">
                <InputGroup size="sm">
                  <Input
                    ref={searchRef}
                    placeholder="Search by name, email..."
                    value={searchQuery}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    pr="2.5rem"
                  />
                  <InputRightElement width="2.5rem">
                    <IconButton
                      aria-label="Search"
                      icon={<Search size={16} />}
                      size="sm"
                      h="1.75rem"
                      w="1.75rem"
                      onClick={handleSearch}
                      variant="ghost"
                    />
                  </InputRightElement>
                </InputGroup>
              </Box>
            </HStack>
          </CardHeader>

          <CardBody
            p={0}
            pt={3}
            pb={4}
            mx={5}
            flex="1"
            overflow="hidden"
            display="flex"
            flexDirection="column"
            minH={0}
            position="relative"
          >
            <Box h="100%" display="flex" flexDirection="column" minH={0}>
              <DataTable
                ref={tableRef}
                options={tableOptions}
                params={tableParams}
                access={permissions}
                customRenderers={{
                  first_name: (value, row) => {
                    const first = row?.first_name ?? ''
                    const last = row?.last_name ?? ''
                    const name = [first, last].filter(Boolean).join(' ') || row?.user_login || '–'
                    const availabilityStatus = row?.availability_status ?? 'offline'
                    const statusColor = availabilityStatus === 'online'
                      ? '#22C55E'
                      : availabilityStatus === 'break'
                        ? '#F59E0B'
                        : availabilityStatus === 'lunch'
                          ? '#EF4444'
                          : '#6B7280' // offline or no record
                    return (
                      <HStack spacing={2} align="center" minW={0}>
                        <Avatar 
                          size="sm" 
                          name={name} 
                          bg={statusColor} 
                          color="white" 
                          flexShrink={0}
                          w="24px"
                          h="24px"
                        />
                        <Tooltip label={name} hasArrow placement="top">
                          <Text as="span" noOfLines={1} display="block" w="100%" minW={0}>
                            {name}
                          </Text>
                        </Tooltip>
                      </HStack>
                    )
                  },
                  user_email: (value) => {
                    const str = value ? String(value) : '–'
                    return (
                      <Tooltip label={str} hasArrow placement="top">
                        <Text as="span" noOfLines={1} display="block" w="100%" minW={0}>
                          {str}
                        </Text>
                      </Tooltip>
                    )
                  },
                  role_name: (value) => (value ? String(value) : '–'),
                  assigned_tickets_count: (value) => {
                    const n = typeof value === 'number' ? value : parseInt(String(value ?? 0), 10)
                    return (
                      <Text as="span" fontWeight="600">
                        {Number.isNaN(n) ? '0' : n}
                      </Text>
                    )
                  },
                }}
              />
            </Box>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  )
}
